import { Link } from 'react-router-dom'
import MetaTags from '../components/MetaTags'
import DinBilpartnerBadge from '../components/DinBilpartnerBadge'
import ReviewsSlider from '../components/ReviewsSlider'
import ContactForm from '../components/ContactForm'
import { imageUrl } from '../lib/images'

export default function VinterdaekPage({ settings }) {
  const bookingUrl = settings?.booking_url || 'https://service.dinbilpartner.dk/book-tid'
  const phone = settings?.phone || '22 18 73 30'
  const phoneRaw = settings?.phone_raw || '22187330'

  return (
    <>
      <MetaTags
        title="Montering af vinterdæk i Hejnsvig. Kontakt os i dag og få et tilbud"
        description="Ønsker du hjælp til montering af vinterdæk i Hejnsvig, Billund, Vorbasse eller Grindsted? Vi har fokus på sikkerhed. Ring til os i dag"
        ogTitle="Montering af vinterdæk i Hejnsvig. Kontakt os i dag og få et tilbud"
        ogDescription="Ønsker du hjælp til montering af vinterdæk i Hejnsvig, Billund, Vorbasse eller Grindsted? Vi har fokus på sikkerhed. Ring til os i dag"
        ogImage={imageUrl('img-7698-min-scaled.jpg', 'width=1200,quality=85,format=auto')}
      />

      {/* Hero Header */}
      <section className="bg-[#14171a] text-white py-14 sm:py-20 border-b border-[#262c33] relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src={imageUrl('img-7698-min-scaled.jpg', 'width=1600,quality=80,format=auto')}
            alt="Montering af vinterdæk"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl space-y-4">
            <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <Link to="/" className="hover:text-white">Forside</Link>
              <span>/</span>
              <span className="text-[#e20019]">Autoværksted</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Montering af vinterdæk med fokus på sikkerhed
            </h1>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
              Professionel service og reparation og skader af alle bilmærker. Speciale i V.A.G
              Gruppen og altid mulighed for lånebil, når din bil er på værksted.
            </p>
            <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white px-6 py-3 rounded-xl font-bold text-sm shadow-md transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-red-500 text-center"
              >
                <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                <span>Book tid til vinterdæk</span>
              </a>
              <a
                href={`tel:${phoneRaw}`}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#20252c] hover:bg-[#2b323c] border border-gray-700 text-white px-5 py-3 rounded-xl font-bold text-sm transition-colors whitespace-nowrap text-center"
              >
                <i className="fa-solid fa-phone text-[#e20019]" aria-hidden="true" />
                <span>Ring på {phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content from Scrape */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column */}
            <div className="lg:col-span-7 space-y-8 text-gray-800 leading-relaxed text-base">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  Gør bilen klar til kulde, sne og glatte veje
                </h2>
                <p>
                  Har du brug for professionel montering af vinterdæk, kan du få hjælp på vores
                  autoværksted hos EK Biler. Vi tager imod biler fra Hejnsvig, Billund, Vorbasse og
                  Grindsted samt de omkringliggende områder. Her bliver dine dæk monteret og
                  kontrolleret grundigt, så bilen er klar til skiftende temperaturer, våde veje og
                  vinterføre.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Få skiftet til vinterdæk på det rigtige tidspunkt
                </h2>
                <p>
                  Timingen har stor betydning, når bilen skal gøres klar til vinteren. En praktisk
                  tommelfingerregel er at bestille tid til montering af vinterdæk, når temperaturen
                  jævnligt nærmer sig 7 grader. Det sker ofte i oktober eller november, men vejret kan
                  naturligvis ændre sig fra år til år.
                </p>
                <p className="mt-3">
                  Et skift til vinterdæk i god tid betyder, at du ikke bliver overrasket af den første
                  frost eller et pludseligt snefald. Vinterdækkenes gummiblanding er udviklet til lave
                  temperaturer, og mønsteret er designet til at give et bedre vejgreb i sne, sjap og på
                  kolde, våde veje.
                </p>
                <p className="mt-3">
                  Hos EK Biler hjælper vi dig med en løsning, der passer til din bil og dit
                  kørselsbehov. Det gælder både almindelige benzin- og dieselbiler samt el- og
                  hybridbiler. Du kan også få specialiseret hjælp, hvis du kører i en bil fra
                  V.A.G.-gruppen, herunder VW, Audi, Seat eller Skoda.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Det kontrollerer vi ved montering af vinterdæk
                </h2>
                <p>
                  Korrekt montering af vinterdæk handler om mere end blot at sætte hjulene på bilen.
                  Dækkenes tilstand, størrelse og placering har betydning for både sikkerhed og
                  kørekomfort. Derfor arbejder vi omhyggeligt og kontrollerer de relevante punkter
                  undervejs.
                </p>
                <ul className="list-disc list-inside space-y-2 mt-4 pl-2 text-gray-700">
                  <li>Kontrol af dækkenes mønster og generelle tilstand</li>
                  <li>Gennemgang af fælge og synlige skader</li>
                  <li>Korrekt montering af hjulene</li>
                  <li>Kontrol og tilpasning af dæktrykket</li>
                  <li>Rådgivning om dæk, størrelse og mærkning</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Vinterdæk med fælge</h2>
                <p>
                  Har du et komplet sæt vinterdæk med fælge, kan hjulene monteres direkte på bilen. Det
                  gør arbejdet mere enkelt og mindsker behovet for at af- og påmontere selve dækket på
                  fælgen hver sæson.
                </p>
                <p className="mt-2">
                  Hvis du mangler plads til hjulene derhjemme, kan du samtidig høre om muligheden for{' '}
                  <Link to="/daekhotel" className="text-[#e20019] font-bold hover:underline">
                    dækhotel
                  </Link>
                  . Så slipper du for at flytte tunge og snavsede hjul frem og tilbage, og sættet ligger
                  klar, næste gang årstiden skifter.
                </p>
                <p className="mt-2 text-sm text-gray-600">
                  Du har altid mulighed for en lånebil, mens din bil er på værksted. Til mindre opgaver
                  kan vores Express-fordelskoncept desuden gøre det lettere at få bilen hurtigt videre,
                  afhængigt af opgaven og den aktuelle kapacitet.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Vælg de rigtige vinterdæk</h2>
                <p>
                  De rigtige vinterdæk afhænger ikke kun af bilmærket. Bilens vægt, dækmål,
                  kørselsmønster og de vejforhold, du oftest møder, bør også indgå i valget. En tung{' '}
                  <Link to="/elbil-service" className="text-[#e20019] font-bold hover:underline">
                    elbil
                  </Link>{' '}
                  kan stille andre krav til dækkenes belastningsevne end en mindre benzinbil.
                </p>
                <ul className="list-disc list-inside space-y-2 mt-3 pl-2 text-gray-700">
                  <li>Korrekt størrelse og belastningsindeks</li>
                  <li>Vådgreb og bremseegenskaber</li>
                  <li>Støjniveau og komfort</li>
                  <li>Mærkning til vinterkørsel</li>
                  <li>Forventet antal kørte kilometer</li>
                </ul>
              </div>

              {/* FAQs from Scrape */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  FAQ – Ofte stillede spørgsmål om montering af vinterdæk
                </h3>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Hvad koster montering af vinterdæk?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Omkostningen afhænger blandt andet af, om dækkene allerede sidder på fælge, bilens
                      hjulstørrelse og sættets tilstand. Det kan også påvirke beløbet, hvis der skal
                      bruges nye ventiler, foretages afbalancering eller leveres nye dæk. Hos EK Biler
                      får du et konkret tilbud ud fra netop din bil og opgaven.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Hvilke vinterdæk er de bedste?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      De bedste vinterdæk er dem, der passer til bilens specifikationer, vægt og din
                      normale kørsel. Vi ser blandt andet på dækmål, belastningsindeks, vådgreb og
                      vintermærkning. Til kørsel i hårdt vinterføre er dæk med 3PMSF-mærket særligt
                      egnede.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Hvilken måned skifter man til vinterdæk?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Mange skifter i oktober eller november, men temperaturen og føret er vigtigere end
                      kalenderen. Det er en god idé at få hjulene på, inden der opstår risiko for sne, is
                      og sjap. Bestiller du tid i god tid, er bilen klar, før vintervejret sætter ind.
                    </p>
                  </div>

                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
                    <h4 className="font-bold text-gray-900 text-sm mb-1">
                      Hvad er reglerne for vinterdæk?
                    </h4>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Dækkene må ikke være åbenbart uegnede til det konkrete føre, og politiet kan
                      udstede en bøde, hvis de er det. Mønsterdybden skal være mindst 1,6 mm, og dæk på
                      samme aksel skal have samme størrelse og type. Vinterdæk er mærket M+S, mens
                      3PMSF-mærket viser, at dækket er særligt egnet til sne og is.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Sticky Booking Card & Contact Form */}
            <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-24">
              <div className="bg-[#14171a] text-white p-6 sm:p-8 rounded-2xl border border-gray-800 space-y-4">
                <div className="text-xs font-bold text-[#e20019] uppercase tracking-wider">
                  Vintersikring
                </div>
                <h3 className="text-2xl font-bold">Montering af vinterdæk</h3>
                <p className="text-sm text-gray-300">
                  Bestil tid online eller ring for et uforpligtende tilbud på montering og nye dæk.
                </p>
                <div className="pt-2">
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full text-center inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white py-3.5 px-6 rounded-xl font-bold text-sm shadow-md transition-all whitespace-nowrap"
                  >
                    <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                    <span>Book tid til vinterdæk</span>
                  </a>
                </div>
              </div>

              <ContactForm
                title="Send en forespørgsel"
                subtitle="Vi vender tilbage for at fuldføre din booking"
                bookingUrl={bookingUrl}
              />
            </div>
          </div>
        </div>
      </section>

      <DinBilpartnerBadge />
      <ReviewsSlider />
    </>
  )
}
